import { useEffect, useRef, useState } from 'react';
import { api, wertDeutsch } from '../api.ts';
import {
  Dialog, Fehlerkasten, Auswahl, Text, ausFehler, type Fehlerlage,
} from '../teile/Formular.tsx';
import { useFrisch } from '../teile/Frisch.tsx';
import { Bereichsleser } from '../teile/Bereichsleser.tsx';

/**
 * EINEN LESER ANLERNEN - AM BEISPIEL.
 *
 * ORIGINALTEXT MARVIN (09.09.2026)
 *   „das erkennen der dokumente ist noch nicht gut, er ließt die daten nicht
 *    sauber aus. da müssen wir was besseres finden. kann man ihm das
 *    beibringen?"
 *
 * Der Weg dahinter steht seit .156 da, die Regeln in `regeln/leseregel.ts`,
 * und bis .161 hat GENAU DIESER BILDSCHIRM GEFEHLT. Ein Weg, den der Server
 * kann und den keine Oberfläche geht, ist von einem nicht gebauten nicht zu
 * unterscheiden - er sieht im Programmcode vollständig aus, weil die teure
 * Hälfte dasteht.
 *
 * ================================================================
 * DIESER BILDSCHIRM RECHNET WEDER ANKER NOCH RICHTUNG.
 *
 * Er schickt die MARKIERUNG (Zeile, von, bis); woraus daraus ein Anker wird,
 * rechnet der Server. Eine zweite Fassung im Browser liefe beim ersten
 * Nachtrag auseinander, und dann speichert jemand eine Regel, die er nie
 * gesehen hat.
 *
 * UND DER PROBELAUF WIRD GEZEIGT UND NICHT GEWORFEN. `taugt: false` ist eine
 * Auskunft; der Riegel greift beim Speichern, und zwar im Server. Eine
 * Vorschau, die abweist, ist keine Vorschau, sondern ein zweiter
 * Speicherversuch.
 */

/** Die Zeichen VOR `knoten`/`versatz`, gemessen gegen die ganze Zeile.
 *
 *  DER VERSATZ WIRD GEGEN DIE ZEILE GERECHNET UND NICHT GEGEN DEN
 *  TEXTKNOTEN. Sobald eine Zeile markiert ist, zeichnet sie der Browser in
 *  drei Stücken (davor, markiert, danach); ein Versatz gegen das eine Stück
 *  wäre beim Nachbessern einer Markierung um die Länge des ersten daneben,
 *  und der Anker käme aus dem falschen Wort. */
function zeichenBis(zeileEl: Element, knoten: Node, versatz: number): number {
  const r = document.createRange();
  r.selectNodeContents(zeileEl);
  r.setEnd(knoten, versatz);
  return r.toString().length;
}

interface Markierung { zeile: number; von: number; bis: number; }

export default function Leseregel(
  { eingang, zu }: { eingang: any; zu: () => void }
) {
  const [d, setD] = useState<any>(null);
  const [feld, setFeld] = useState('');
  const [mark, setMark] = useState<Markierung | null>(null);
  const [markgrund, setMarkgrund] = useState('');
  const [probe, setProbe] = useState<any>(null);
  const [probeLaeuft, setProbeLaeuft] = useState(false);
  const [fehler, setFehler] = useState<Fehlerlage | null>(null);
  const [laeuft, setLaeuft] = useState(false);
  /* ART UND ABSENDER SETZT DER MENSCH, WENN SIE NICHT DASTEHEN.
     Marvin, 22.09.2026: „wenn er sie dann nicht zuordnen kann, dann kommt
     der leser, den ich öffnen kann." Genau dann steht die Art auf
     „unbekannt" und bei einer hochgeladenen Datei fehlt der Absender.
     Leer heisst hier: nimm, was am Eingang steht. */
  const [art, setArt] = useState('');
  const [absender, setAbsender] = useState('');
  /* AM BILD ODER AM TEXT (.271). Ein Scan ohne brauchbaren Text wird am Bild
     angelernt; Marvin: „der leser um was anzulernen ist ja absolut unbrauchbar". */
  const [weise, setWeise] = useState<'bild' | 'text' | null>(null);

  async function laden(a = art, b = absender) {
    try {
      setD(await api.leseregelEingang(eingang.id, a, b));
      setFehler(null);
    } catch (f) { setFehler(ausFehler(f)); }
  }
  useEffect(() => { laden(); }, [eingang.id]);
  useFrisch(laden);

  /* GEGEN ÜBERHOLENDE ANTWORTEN. Wer die Markierung schnell nachbessert,
     stösst mehrere Probeläufe an; ohne diesen Zähler gewinnt die Antwort, die
     zuletzt ankommt, und das ist nicht die zur letzten Markierung. */
  const lauf = useRef(0);

  useEffect(() => {
    if (!mark || !feld) { setProbe(null); return; }
    const meins = ++lauf.current;
    setProbeLaeuft(true);
    api.leseregelProbe({
      eingang_id: eingang.id, feld,
      zeile: mark.zeile, von: mark.von, bis: mark.bis,
      art: art || undefined, absender: absender || undefined,
    })
      .then((a: any) => { if (meins === lauf.current) setProbe(a); })
      .catch((f: any) => {
        if (meins === lauf.current) { setProbe(null); setFehler(ausFehler(f)); }
      })
      .finally(() => { if (meins === lauf.current) setProbeLaeuft(false); });
  }, [mark, feld, eingang.id, art, absender]);

  function markieren() {
    const s = document.getSelection();
    if (!s || s.isCollapsed || s.rangeCount < 1) return;
    const r = s.getRangeAt(0);

    const anfang = (r.startContainer as any).parentElement?.closest?.('.txt');
    const ende = (r.endContainer as any).parentElement?.closest?.('.txt');
    if (!anfang || !ende) return;

    /* ANFANG UND ENDE MÜSSEN IN DERSELBEN ZEILE LIEGEN. Der Server rechnet den
       Anker aus EINER Zeile; eine Markierung über zwei Zeilen hätte kein
       „links davon". Sie wird nicht heimlich gekürzt, sondern der Grund steht
       daneben - eine stillschweigend geänderte Markierung ist eine Regel, die
       jemand nie gesehen hat. */
    if (anfang !== ende) {
      setMark(null);
      setMarkgrund('Markieren Sie den Wert INNERHALB einer Zeile.');
      return;
    }

    const zeile = Number(anfang.getAttribute('data-zeile'));
    const von = zeichenBis(anfang, r.startContainer, r.startOffset);
    const bis = zeichenBis(anfang, r.endContainer, r.endOffset);
    if (!Number.isFinite(zeile) || bis <= von) return;

    setMarkgrund('');
    setMark({ zeile, von, bis });
  }

  async function speichern() {
    if (!mark || !feld) return;
    setLaeuft(true);
    try {
      await api.leseregelSpeichern({
        eingang_id: eingang.id, feld,
        zeile: mark.zeile, von: mark.von, bis: mark.bis,
        art: art || undefined, absender: absender || undefined,
      });
      setMark(null);
      setProbe(null);
      setFehler(null);
      await laden();
    } catch (f) { setFehler(ausFehler(f)); }
    finally { setLaeuft(false); }
  }

  async function wegnehmen(id: string) {
    setLaeuft(true);
    try { await api.leseregelWeg(id); setFehler(null); await laden(); }
    catch (f) { setFehler(ausFehler(f)); }
    finally { setLaeuft(false); }
  }

  const zeilen: string[] = d?.zeilen ?? [];
  const felder: any[] = d?.felder ?? [];
  const regeln: any[] = d?.regeln ?? [];
  const feldtitel = felder.find((f) => f.schluessel === feld)?.titel ?? '';

  return (
    <Dialog
      titel="Leser anlernen"
      untertitel={eingang.dateiname}
      voll laeuft={laeuft} zu={zu}
    >
      <Fehlerkasten lage={fehler} />

      {/* ====================================================================
          WOFÜR DIE REGEL GILT - UND ZWAR AUCH DANN, WENN DER SERVER NOCH
          NEIN SAGT.
          ====================================================================
          ORIGINALTEXT MARVIN (22.09.2026)
            „da soll die datei eingelesen werden und wenn er sie dann nicht
             zuordnen kann, dann kommt der leser, den ich öffnen kann."

          Genau bei einer Datei, die liegengeblieben ist, steht die Art auf
          „noch nicht bestimmt", und eine hochgeladene Datei hat gar keinen
          Absender. Bis heute öffnete sich der Leser dann nicht.

          **DIESE 2 FELDER STEHEN AUCH DANN DA, WENN DER TEXT NOCH NICHT
          GELADEN IST** - sonst wäre die einzige Handlung nach einem 409 das
          Schliessen, und der Mensch käme nie an die Stelle, an der er es
          richtigstellen kann. */}
      <div className="karte" style={{ marginBottom: 12 }}>
        <b>Wofür die Regel gilt</b>
        <div className="reihe" style={{ marginTop: 8, alignItems: 'flex-end' }}>
          <Auswahl
            name="art" titel="Art des Vordrucks"
            wert={art || d?.eingang?.art || ''}
            setze={(w: string) => { setArt(w); laden(w, absender); }}
            breite={260} leerText="wie erkannt"
            optionen={(d?.arten ?? []).map((a: any) => ({
              wert: a.schluessel, text: a.titel,
            }))}
          />
          <Text
            name="absender" titel="Von wem der Vordruck ist"
            wert={absender || d?.absender || ''}
            setze={setAbsender}
            hinweis={d?.absender_woher
              ? `zurzeit ${d.absender_woher}` : 'Mailadresse oder Netzadresse'}
          />
          <button className="knopf klein" disabled={laeuft}
                  onClick={() => laden(art, absender)}>
            Übernehmen
          </button>
        </div>
      </div>

      {!d && <div className="leise">Wird geladen …</div>}

      {d && (
        <div className="reihe" style={{ gap: 6, marginBottom: 10 }}>
          <button className={'knopf' + ((weise ?? ((d.zeilen ?? []).length < 8 ? 'bild' : 'text')) === 'bild' ? ' haupt' : '')}
                  onClick={() => setWeise('bild')}>Am Bild (Scan)</button>
          <button className={'knopf' + ((weise ?? ((d.zeilen ?? []).length < 8 ? 'bild' : 'text')) === 'text' ? ' haupt' : '')}
                  onClick={() => setWeise('text')}>Am Text</button>
        </div>
      )}

      {d && (weise ?? ((d.zeilen ?? []).length < 8 ? 'bild' : 'text')) === 'bild' && (
        <Bereichsleser eingangId={eingang.id} art={art || d?.eingang?.art || ''} vordruck={d.absender_schluessel ?? ''} />
      )}

      {d && (weise ?? ((d.zeilen ?? []).length < 8 ? 'bild' : 'text')) === 'text' && (
        <>
          <div className="hinweis">
            <b>So geht es</b>
            {' '}Den Wert im Text markieren, rechts das Feld wählen, den
            Probelauf ansehen, dann speichern. Gelernt wird die STELLE
            („nach welchem Wort") und nie der Wert selbst - sonst stünde beim
            nächsten Bescheid derselben Behörde der Wert von heute in der Akte.
            Die Regel gilt für jedes Papier dieser Art von
            {' '}<b>{d.absender_schluessel}</b>.
          </div>

          <div className="nebeneinander breitschmal">
            <div>
              <h3>Der erkannte Text</h3>

              {d.grund && <div className="hinweis">{d.grund}</div>}

              {!d.grund && (
                <div className="lesetext rollt" onMouseUp={markieren}>
                  {zeilen.map((z, i) => {
                    const dran = mark?.zeile === i;
                    return (
                      <div className="lesezeile" key={i}>
                        <span className="nr">{i + 1}</span>
                        <span className="txt" data-zeile={i}>
                          {dran ? (
                            <>
                              {z.slice(0, mark!.von)}
                              <mark className="markiert">
                                {z.slice(mark!.von, mark!.bis)}
                              </mark>
                              {z.slice(mark!.bis)}
                            </>
                          ) : z}
                        </span>
                      </div>
                    );
                  })}
                </div>
              )}

              {markgrund && <div className="hinweis fehler">{markgrund}</div>}
            </div>

            <div>
              <h3>Das Feld</h3>
              <Auswahl
                name="feld" titel="In welches Feld gehört der Wert?"
                wert={feld} setze={setFeld} breite={300}
                leerText="bitte wählen"
                optionen={felder.map((f) => ({
                  wert: f.schluessel, text: f.titel,
                }))}
              />

              <h3>Der Probelauf</h3>

              {!mark && (
                <div className="leise">
                  Markieren Sie links den Wert, der in das Feld gehört.
                </div>
              )}
              {mark && !feld && (
                <div className="leise">Wählen Sie oben das Feld.</div>
              )}
              {mark && feld && probeLaeuft && (
                <div className="leise">Wird gerechnet …</div>
              )}

              {mark && feld && !probeLaeuft && probe && (
                <>
                  {probe.regel && (
                    <div className="hinweis">
                      <b>Die Regel</b>
                      {' '}Der Wert für „{feldtitel}" steht
                      {probe.regel.richtung === 'darunter'
                        ? ' in der Zeile UNTER '
                        : ' rechts neben '}
                      „{probe.regel.anker}".
                    </div>
                  )}

                  <div className={'hinweis' + (probe.taugt ? '' : ' fehler')}>
                    {probe.grund}
                  </div>

                  <div className="leise">
                    Getroffen: {probe.getroffen} von {probe.funde.length}
                    {' '}Papier(en)
                    {probe.gedeckelt > 0
                      ? `, ${probe.gedeckelt} weitere nicht angesehen`
                      : ''}
                  </div>

                  {probe.funde.length > 0 && (
                    <div className="rollt">
                      <table className="liste">
                        <thead>
                          <tr>
                            <th>Datei</th><th>Gelesen</th><th>Befund</th>
                          </tr>
                        </thead>
                        <tbody>
                          {probe.funde.map((f: any, i: number) => (
                            <tr key={i}>
                              <td>{f.dateiname}</td>
                              <td>{f.wert === null || f.wert === ''
                                ? '-' : wertDeutsch(f.wert)}</td>
                              <td className="klein">{f.grund || f.roh}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </>
              )}

              <div className="reihe" style={{ marginTop: 14 }}>
                <button className="knopf haupt"
                        disabled={laeuft || !mark || !feld}
                        onClick={speichern}>
                  Regel speichern
                </button>
                {mark && (
                  <button className="knopf" disabled={laeuft}
                          onClick={() => { setMark(null); setProbe(null); }}>
                    Markierung weg
                  </button>
                )}
              </div>

              <h3>Was dieser Absender schon gelernt hat</h3>
              {regeln.length === 0 && (
                <div className="leise">Für {d.absender_schluessel} steht noch
                  keine Regel da.</div>
              )}
              {regeln.length > 0 && (
                <div className="rollt">
                  <table className="liste">
                    <thead>
                      <tr><th>Feld</th><th>Steht</th><th /></tr>
                    </thead>
                    <tbody>
                      {regeln.map((r: any) => (
                        <tr key={r.id}>
                          <td>
                            {felder.find((f) => f.schluessel === r.feld)?.titel
                              ?? r.feld}
                          </td>
                          <td className="klein">
                            {r.richtung === 'darunter'
                              ? 'unter ' : 'rechts von '}
                            „{r.anker}"
                          </td>
                          <td className="knopfleiste">
                            <button className="knopf klein" disabled={laeuft}
                                    onClick={() => wegnehmen(r.id)}>
                              weg
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        </>
      )}
    </Dialog>
  );
}
